import { type ButtonHTMLAttributes, forwardRef } from 'react'

/**
 * Touch-first button.
 *
 * Three rules baked in:
 *   1. **Minimum 48px height** (`min-h-[3rem]`) on every variant —
 *      Apple HIG / Material Design touch target. A driver in a
 *      bouncing truck can't reliably tap a 32px button.
 *   2. **Active state visible** (`active:scale-[0.97]`) — instant
 *      tactile feedback, no waiting for the network.
 *   3. **Disabled state announces itself** — `aria-disabled`,
 *      reduced opacity, no pointer cursor. Loading state pairs
 *      with a spinner sibling that the caller renders.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  fullWidth?: boolean
  loading?: boolean
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-brand text-brand-fg hover:bg-brand-strong active:bg-brand-strong shadow-sm',
  secondary:
    'bg-surface text-brand border-2 border-brand hover:bg-info-soft active:bg-info-soft',
  ghost:
    'bg-transparent text-brand hover:bg-info-soft active:bg-info-soft',
  danger:
    'bg-danger text-brand-fg hover:opacity-90 active:opacity-90 shadow-sm',
}

const SIZE_CLASSES: Record<Size, string> = {
  md: 'min-h-[3rem] px-4 text-base',
  lg: 'min-h-[3.5rem] px-6 text-lg',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      disabled,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const isDisabled = disabled === true || loading
    return (
      <button
        ref={ref}
        type="button"
        aria-disabled={isDisabled || undefined}
        disabled={isDisabled}
        className={[
          'inline-flex items-center justify-center gap-2',
          'rounded-xl font-medium',
          'transition-all duration-100',
          'active:scale-[0.97]',
          'disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          fullWidth ? 'w-full' : '',
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          className ?? '',
        ].join(' ')}
        {...rest}
      >
        {children}
      </button>
    )
  },
)
